import { db, firebaseImport } from '@/js/external-js/config/main_config';
import { isStandardEnabled } from '@/js/external-js/variableModules/utils/EnabledModulesManager';
import { ContentFilters } from '@/js/external-js/variableModules/utils/ContentFilters.util';
import { populateTooltips } from '@/js/external-js/variableModules/utils/populateTooltips.msg.util';
import { sanitise } from '@/js/external-js/variableModules/utils/sanitise.util';
import { getEnabledModules } from '@/js/external-js/variableModules/utils/EnabledModulesManager';
import { parseInterestedParties } from '@/js/external-js/variableModules/utils/getInterestedParties.util';

export async function getAllModules() {
  const enabledModules = await getEnabledModules();
  console.log('[getAllModules]', enabledModules);

  const allModules = {
    ISO9001: {
      enabled: enabledModules.has('iso9001'),
      name: 'Quality Management Systems Requirements',
      db: 'iso9001',
      msg: true,
    },
    ISO45001: {
      enabled: enabledModules.has('iso45001'),
      name: 'Occupational Health and Safety Management Systems',
      db: 'iso45001',
      msg: true,
    },
    ISO27001: {
      enabled: enabledModules.has('iso27001-2013'),
      name: 'Information Security Management Systems',
      db: 'iso27001',
      msg: true,
    },
    SMB1001: {
      enabled: enabledModules.has('smb1001'),
      name: 'Small Business Standard',
      db: 'smb1001',
      msg: false,
    },
    SOC2: {
      enabled: enabledModules.has('soc2'),
      name: 'System and Organization Controls (SOC 2)',
      db: 'soc2',
      msg: false,
    },
    DISP: {
      enabled: enabledModules.has('disp'),
      name: 'Defence Industry Security Program',
      db: 'disp',
      msg: false,
    },
    ESSENTIAL8: {
      enabled: enabledModules.has('essential8'),
      name: 'Australian Cyber Security Centre Essential Eight',
      db: 'essential8',
      msg: false,
    },
    PA: {
      enabled: enabledModules.has('pa'),
      name: 'Privacy Act Compliance',
      db: 'pa',
      msg: false,
    },
    ISO42001: {
      enabled: enabledModules.has('iso42001'),
      name: 'Artificial Intelligence Management Systems',
      db: 'iso42001',
      msg: true,
    },
    ISO27701: {
      enabled: enabledModules.has('iso27701'),
      name: 'Privacy Information Management Systems',
      db: 'iso27701',
      msg: true,
    },
    ISO14001: {
      enabled: enabledModules.has('iso14001'),
      name: 'Environmental Management Systems',
      db: 'iso14001',
      msg: true,
    },
    ISM: {
      enabled: enabledModules.has('ism'),
      name: 'Information Security Manual',
      db: 'ism',
      msg: false,
    },
  };

  const allModulesObj = Object.entries(allModules)
    .filter(([, module]) => module.enabled)
    .map(([key, module]) => ({
      id: key,
      ...module
    }));

  console.log('[/msg/utils/common.ts:getAllModules]', allModulesObj);
  return allModulesObj;
}

export async function processContent(data: any, pathArray: string[]): Promise<string | null> {
  // Traverse the data using the keys in pathArray
  for (const key of pathArray) {
    if (data && Object.prototype.hasOwnProperty.call(data, key)) {
      data = data[key];
    } else {
      console.error(`Path not found: ${key}`);
      return null;
    }
  }

  // Retrieve the 'content' field at the end of the path
  if (data && Object.prototype.hasOwnProperty.call(data, 'content')) {
    return data['content'].replace(/\\r\\n|\\r|\\n/g, '<br>');
  } else {
    console.error('No content field at the end of the path');
    return null;
  }
}

// From: src/js/external-js/variableModules/utils/docsByRoute.util.ts
export function filterDocsByRoute(uploadedDocs: any, msgPageRoute: string, procRefs: string[]): firebaseImport.firestore.DocumentData[] {
  const uniqueDocs = new Map<string, firebaseImport.firestore.DocumentData>();

  // Normalize inputs
  const normRoute = (v: any) => (v ?? '').toString().trim().toLowerCase();
  const normVal = (v: any) => (v ?? '').toString().trim().toLowerCase();
  const normalizedRoute = normRoute(msgPageRoute);
  const normalizedProcRefs = (Array.isArray(procRefs) ? procRefs : [])
    .map(normVal)
    .filter(Boolean);

  const docs = uploadedDocs && typeof uploadedDocs === 'object' ? uploadedDocs : {};

  for (const docKey in docs) {
    if (!Object.prototype.hasOwnProperty.call(docs, docKey)) continue;
    const document = docs[docKey] || {};
    const docId = document.docID || docKey;

    if (uniqueDocs.has(docId)) continue;

    // msgPages can be string or array
    const msgPagesArr: string[] = Array.isArray(document.msgPages)
      ? document.msgPages
      : (document.msgPages ? [document.msgPages] : []);
    const matchesByPageRoute = msgPagesArr
      .map(normRoute)
      .includes(normalizedRoute);

    // Gather all possible control/reference arrays (procRef, procRefs, relatedControls, selectedControls)
    const flattenToStrings = (val: any): string[] => {
      if (!val) return [];
      if (Array.isArray(val)) {
        return val
          .flatMap((x) => {
            if (x == null) return [];
            if (typeof x === 'object' && 'id' in x) return [(x as any).id];
            return [x];
          })
          .map(normVal)
          .filter(Boolean);
      }
      return [normVal(val)].filter(Boolean);
    };

    const byProcRef = flattenToStrings(document.procRef);
    const byProcRefs = flattenToStrings(document.procRefs);
    const byRelated = flattenToStrings(document.relatedControls);
    const bySelected = flattenToStrings(document.selectedControls);

    // Build a Set of all refs on the document
    const docRefs = new Set<string>([...byProcRef, ...byProcRefs, ...byRelated, ...bySelected]);

    // DED-1202: Support parent-child clause matching
    // e.g., document with "6.1" should match page clauses "6.1.1", "6.1.2", etc.
    // and document with "6.1.1" should match page clause "6.1"
    const clauseMatches = (clauseA: string, clauseB: string): boolean => {
      if (clauseA === clauseB) return true;
      // Check if clauseA is a parent of clauseB (e.g., "6.1" matches "6.1.1")
      if (clauseB.startsWith(clauseA + '.')) return true;
      // Check if clauseB is a parent of clauseA (e.g., "6.1.1" matches "6.1")
      if (clauseA.startsWith(clauseB + '.')) return true;
      return false;
    };

    const matchesByRefs = normalizedProcRefs.some((pageRef) => 
      Array.from(docRefs).some((docRef) => clauseMatches(pageRef, docRef))
    );

    if (matchesByPageRoute || matchesByRefs) {
      uniqueDocs.set(docId, document);
    }
  }

  return Array.from(uniqueDocs.values());
}

// GPT function converted from src/js/external-js/variableModules/utils/ContentFilters.util.ts:260
export function renderClausesAsBadges(html: string): string {
  const clausePattern = /\[Clause\s+\d+(?:\.\d+)+:\s+[^\]]+(?:;\s*Clause\s+\d+(?:\.\d+)+:\s+[^\]]+)*\]/g;

  return html.replace(clausePattern, (match) => {
    const clauseBlock = match.slice(1, -1); // Remove surrounding brackets
    const individualClauses = clauseBlock.split(/\s*;\s*Clause\s+/).map((clause, i) => {
      console.log('[renderClausesAsBadges]', clause);
      const fullClause = i === 0 ? clause : `Clause ${clause}`;
      const clauseID = fullClause.match(/Clause\s+(\d+(?:\.\d+)+)\b/)?.[1];
      if (!clauseID) return ''; // Skip invalid

      return `<span class="badge clause" data-clause-id="${clauseID}">${fullClause.trim()}</span>`;
    });

    return individualClauses.join(' ');
  });
}

// src/components/relatedControls.component71
export function processControlReferences(controlRefs, frameworkMap, isStandardSpecific, standardKey) {
  if (!controlRefs || !Array.isArray(controlRefs)) return;

  const clauseTexts = new Map();

  controlRefs.forEach(function(ref) {
    console.log('Processing reference:', ref);
    ref.frameworks.forEach(function(framework) {
      if (!frameworkMap.has(framework)) {
        frameworkMap.set(framework, new Set());
      }
      frameworkMap.get(framework).add(ref.controlID);

      // Store text (either custom from standardSpecific or default from root)
      if (ref.text) {
        const key = framework + ':' + ref.controlID;
        if (isStandardSpecific || !clauseTexts.has(key)) {
          clauseTexts.set(key, ref.text);
          // console.log((isStandardSpecific ? 'Custom' : 'Default') + ' text stored for', key, ':', ref.text);
        }
      }
    });
  });
  console.log('[processControlReferences]', clauseTexts);
  return clauseTexts;
}
// from src/components/relatedControls.component:33
export function getStandardName(framework, dynamicStandardNames) {
  // First check our dynamic names map
  if (dynamicStandardNames.has(framework)) {
    const displayName = dynamicStandardNames.get(framework);
    // Check if the display name already includes the framework
    if (!displayName.includes(framework)) {
      // If not, prepend the framework name
      return framework + ' - ' + displayName;
    }
    return displayName;
  }

  // Fallback to hardcoded names
  const standardNames = {
    'ISO 9001': 'ISO 9001 - Quality Management Systems Requirements',
    'ISO 27001': 'ISO 27001 - Information Security Management Systems Requirements',
    'ISO 27701': 'ISO 27701 - Privacy Information Management Systems Requirements',
    'ISO 42001': 'ISO 42001 - AI Management Systems Requirements'
  };
  return standardNames[framework] || framework;
}

export const fetchRelatedDocuments = (
  db: any,
  tenancyId: string,
  pageRoute: string,
  setDocs: (docs: any[]) => void,
  procRefs: string[]
) => {
  const relatedDocRef = db.doc(`/customers/${tenancyId}/product/policyPackages/ISO27001keyDocuments/otherDocs`);

  const unsubscribe = relatedDocRef.onSnapshot((doc: any) => {
    if (!doc.exists) return;
    const data = doc.data() || {};
    const uploaded = data.uploaded || {};
    const matchingDocs = filterDocsByRoute(uploaded, pageRoute, procRefs || []);
    setDocs(matchingDocs);
  });

  return unsubscribe;
};

// TODO: This should be removed after MDX implimentation (ie. no longer required)
// src/components/relatedControls.component:195
export const populateStandardsList = async (
  db,
  pageName: string,
  contentPath: string[],
  setDynamicStandardNames: (map: Map<string, string>) => void,
  setSortedFrameworks: (list: string[]) => void
) => {
  getAllModules();
  const docRef = db
    .collection('ManagementSystemGuide')
    .doc('defaults')
    .collection('pages')
    .doc(pageName);

  const doc = await docRef.get();
  if (!doc.exists) {
    console.error('Document does not exist');
    return;
  }

  const data = doc.data();
  let overview = data;
  for (const key of contentPath) {
    if (overview && Object.prototype.hasOwnProperty.call(overview, key)) {
      overview = overview[key];
    } else {
      console.error('Path not found: ' + key);
      return;
    }
  }

  const dynamicStandardNames = new Map<string, string>();
  if (overview.standardSpecific) {
    Object.entries(overview.standardSpecific).forEach(([standard, data]: any) => {
      if (data.displayName) {
        const framework = standard.toUpperCase().replace(/^ISO/, 'ISO ');
        dynamicStandardNames.set(framework, data.displayName);
      }
    });
  }
  setDynamicStandardNames(dynamicStandardNames);

  const frameworkMap = new Map();
  processControlReferences(overview.controlReferences, frameworkMap, false, '');

  const allFrameworks = Array.from(frameworkMap.keys());
  const enabledFrameworks = [];

  for (const framework of allFrameworks) {
    if (await isStandardEnabled(framework)) {
      enabledFrameworks.push(framework);
    }
  }

  const sortedFrameworks = enabledFrameworks.sort();
  setSortedFrameworks(sortedFrameworks);
  console.log('[/msg/utils/common.ts:populateStandardsList]', sortedFrameworks);

};

export const populateCustomContent = (
  db,
  tenancyId,
  pageRoute,
  setContent
) => {
  const editorDocRef = db
    .collection('customers')
    .doc(tenancyId)
    .collection('product')
    .doc('managementSystemGuide')
    .collection('editor')
    .doc('content');

  const unsubscribe = editorDocRef.onSnapshot((doc) => {
    if (!doc.exists) return;
    const data = doc.data();
    const overviewContent = data?.[pageRoute]?.content ?? '';
    setContent(overviewContent);
  });

  return unsubscribe;
};

// export function fetchPageContentMDX(
//   db,
//   pageName,
//   contentPath,
//   activeTenancy,
//   setPageTemplate
// ) {
//   const contentDocRef = db
//     .collection('ManagementSystemGuide')
//     .doc('defaults')
//     .collection('pages')
//     .doc(pageName);

//   const unsubscribe = contentDocRef.onSnapshot(async (doc) => {
//     const data = doc.data();
//     if (!data) return;

//     console.log('[PageData]', data[contentPath].content);

//     setPageTemplate(data[contentPath].content ?? '');
//   });

//   return unsubscribe;
// }

export function fetchPageContentMDX(
  db,
  pageName,
  contentPath,
  activeTenancy,
  setPageTemplate
) {
  const contentDocRef = db
    .collection('ManagementSystemGuide')
    .doc('defaults')
    .collection('pages')
    .doc(pageName);

  const unsubscribe = contentDocRef.onSnapshot(async (doc) => {
    const data = doc.data();
    if (!data) {
      console.warn('No data found in document');
      setPageTemplate('');
      return;
    }

    console.log('Content path received:', contentPath); // Debug log
    console.log('Document data keys:', Object.keys(data)); // Debug log

    let content;
    
    try {
      // If contentPath is an array, join it with dots for consistent handling
      const path = Array.isArray(contentPath) 
        ? contentPath.join('.') 
        : contentPath;

      // Now handle as string path
      if (typeof path === 'string' && path) {
        // Special handling for direct content access
        if (path in data && data[path]?.content) {
          content = data[path].content;
        } 
        // Handle dot notation
        else {
          content = path.split('.').reduce((obj, key) => {
            const nextObj = obj?.[key];
            return nextObj?.content ?? nextObj;
          }, data);
        }
      }
      // Fallback to root content or first available content
      else if (data.content) {
        content = data.content;
      } else {
        // Find first nested content
        const nestedWithContent = Object.values(data).find(
          value => value && typeof value === 'object' && 'content' in value
        );
        content = nestedWithContent?.content;
      }

      if (content !== undefined) {
        console.log('Successfully found content at path:', path);
        setPageTemplate(content);
      } else {
        console.warn('No content found. Available keys in document:', Object.keys(data));
        setPageTemplate('');
      }
    } catch (error) {
      console.error('Error accessing content:', error);
      setPageTemplate('');
    }
  });

  return unsubscribe;
}

export const fetchPageContent = (
  db,
  pageName,
  contentPath,
  activeTenancy,
  setPageTemplate
) => {
  const contentDocRef = db
    .collection('ManagementSystemGuide')
    .doc('defaults')
    .collection('pages')
    .doc(pageName);

  const unsubscribe = contentDocRef.onSnapshot(async (doc) => {
    const data = doc.data();
    if (!data) return;

    let content = await processContent(data, contentPath);
    const contentFilters = new ContentFilters(null);

    content = contentFilters.replacePlaceholder('Company Name', activeTenancy.legalName, content);
    content = contentFilters.replaceWithRegex(/acme[\s,]*inc\.?/gi, activeTenancy.legalName, content);

    let controlRefs;
    if (Array.isArray(data[contentPath[0]]['controlReferences'])) {
      controlRefs = data[contentPath[0]]['controlReferences'].map((ref: any) => ({
        controlID: ref.controlID,
        frameworks: ref.frameworks,
        ref: ref.ref,
        text: ref.text
      }));
    }

    content = populateTooltips(content, controlRefs);
    content = renderClausesAsBadges(content);

    setPageTemplate(content ?? '');
  });

  return unsubscribe;
};

// src/js/external-js/variableModules/utils/fetchIssueRegister.util.js:268
export async function fetchIssueRegisterItems(db, companyID, setIssueRegisterItems) {
  const documentRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/issueRegister/register`);
  const oldDocumentRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);

  // src/js/external-js/variableModules/utils/fetchIssueRegister.util.js:333
  async function getOldIssues() {
    try {
      const oldDocSnapshot = await oldDocumentRef.get();
      const oldData = oldDocSnapshot.exists ? oldDocSnapshot.data() : {};

      const issuesExternal = oldData.issuesExternal || {};
      const issuesInternal = oldData.issuesInternal || {};

      return { issuesExternal, issuesInternal };
    } catch (error) {
      console.error("Error fetching old data from Firestore:", error);
      return { issuesExternal: {}, issuesInternal: {} };
    }
  }

  // Function to convert old issues to the new format
  // src/js/external-js/variableModules/utils/fetchIssueRegister.util.js:310
  function convertOldIssueToNewFormat(oldIssue, type, issueKey) {
    return {
      // id will be assigned later
      name: oldIssue.issue || 'Unknown',
      description: '',
      dateAdded: oldIssue.dateAdded || '',
      type: type, // 'External' or 'Internal'
      details: {
        issueType: '', // We might not have this info
        risks: [],
        objectives: [],
        legalObligations: [],
        frameworks: []
      },
      origin: {
        oldDocument: true,
        issueKey: issueKey,
        map: type === 'External' ? 'issuesExternal' : 'issuesInternal'
      }
    };
  }

  // Function to process and set issues
  const processAndSetIssues = async () => {
    try {
      // Always try to get old issues first
      const { issuesExternal, issuesInternal } = await getOldIssues();
      let oldIssuesList = [];
      let newIssuesList = [];
      let existingIds = new Set();

      // Process old issues
      const tempOldIssuesList = [];
      for (const [issueKey, issueData] of Object.entries(issuesExternal)) {
        const newIssue = convertOldIssueToNewFormat(issueData, 'External', issueKey);
        tempOldIssuesList.push(newIssue);
      }

      for (const [issueKey, issueData] of Object.entries(issuesInternal)) {
        const newIssue = convertOldIssueToNewFormat(issueData, 'Internal', issueKey);
        tempOldIssuesList.push(newIssue);
      }

      // Try to get new issues if document exists
      try {
        const docSnapshot = await documentRef.get();
        if (docSnapshot.exists) {
          const newIssuesData = docSnapshot.data();
          newIssuesList = Object.values(newIssuesData);
          existingIds = new Set(newIssuesList.map(issue => issue.id));

          // Filter out old issues that are already in the new format
          const originsInNewIssues = newIssuesList
            .filter(issue => issue.origin?.oldDocument)
            .map(issue => ({
              map: issue.origin.map,
              issueKey: issue.origin.issueKey
            }));

          oldIssuesList = tempOldIssuesList.filter(oldIssue => {
            const origin = oldIssue.origin;
            if (!origin) return true;
            return !originsInNewIssues.some(newOrigin =>
              newOrigin.map === origin.map && newOrigin.issueKey === origin.issueKey
            );
          });
        } else {
          // If document doesn't exist, use all old issues
          oldIssuesList = tempOldIssuesList;
        }
      } catch (error) {
        console.error('Error fetching new issues:', error);
        // If there's an error, still try to show old issues
        oldIssuesList = tempOldIssuesList;
      }

      // Assign IDs to old issues
      let idCounter = 1;
      for (let oldIssue of oldIssuesList) {
        let newId;
        do {
          newId = `ISU-${idCounter}`;
          idCounter++;
        } while (existingIds.has(newId));

        oldIssue.id = newId;
        existingIds.add(newId);
      }

      // Combine and sort issues
      const combinedIssues = [...newIssuesList, ...oldIssuesList].sort((a, b) => {
        return parseInt(a.id.split('-')[1]) - parseInt(b.id.split('-')[1]);
      });

      setIssueRegisterItems(combinedIssues);
    } catch (error) {
      console.error('Error processing issues:', error);
      setIssueRegisterItems([]);
    }
  };

  // Initial load
  processAndSetIssues();

  // Set up listener for changes
  const unsubscribe = documentRef.onSnapshot(
    () => processAndSetIssues(),
    error => console.error('Error in snapshot listener:', error)
  );

  return unsubscribe;
}

// src/js/external-js/variableModules/utils/parseIssueRegisterRows.util.js:73
export function renderArray(arr, property = null, tag = null) {
console.log("Rendering array:", {arr, property, tag});
/*
{
    "arr": [
        {
            "dateAdded": "2025-09-02T01:25:51.284Z",
            "id": "RSK-5",
            "risk": "Risk RSK-5"
        },
        {
            "dateAdded": "2025-09-02T01:25:51.284Z",
            "risk": "Risk RSK-1",
            "id": "RSK-1"
        }
    ],
    "property": "riskID",
    "tag": "RSK"
}
    */
  return arr.map(item => {
    if (typeof item === 'object' && !Array.isArray(item)) {
      // If it's an object and you want to display the specific property or full object values
      let content = property ? item[property] : Object.values(item).join(', ');
      if (property === 'standard') {
        // Check if content already has the tag
        if (content.includes(tag)) {
          return sanitise(content);
        }
        // If the item is a standard, display it as a badge
        return `${tag ? `${tag}-${sanitise(content)}` : sanitise(content)}`;
      }

      if (content === undefined || content === null) {
        return '';
      }

      if (content.includes(tag)) {
        return sanitise(content);
      }
      return `${tag ? `${tag}-${sanitise(content)}` : sanitise(content)}`;
    }

    if (property === 'standard') {
      // If the item is a standard, display it as a badge
      if (item.includes(tag)) {
        return sanitise(item);
      }
      return `${tag && !sanitise(item).includes(tag) ? `${tag}-${sanitise(item)}` : sanitise(item)}`;
    }

    // If it's a primitive value (string, number, etc.), just display it directly
    if (item.includes(tag)) {
      return sanitise(item);
    }
    return `${tag && !sanitise(item).includes(tag) ? `${tag}-${sanitise(item)}` : sanitise(item)}`;
  }).join(property === 'standard' ? ' ' : ', '); // Separate each <a> tag by a comma and a space
}

// src/js/external-js/variableModules/utils/getInterestedParties.util.ts:41
// Delete Interested Party supporting both new and old layouts
export async function deleteInterestedParty(db, companyID, partyId) {
  // Try new layout first
  try {
    const newDocRef = db.collection('customers')
      .doc(companyID)
      .collection('product')
      .doc('managementSystemGuide')
      .collection('interestedParties')
      .doc(partyId.toString());
    const newDocSnap = await newDocRef.get();
    if (newDocSnap.exists) {
      await newDocRef.delete();
      return;
    }
  } catch (e) {
    // Continue to old layout
  }
  // Old layout: partiesInterested is an object in ismsManual
  const oldDocRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);
  const oldDocSnap = await oldDocRef.get();
  if (oldDocSnap.exists) {
    const data = oldDocSnap.data();
    if (data.partiesInterested && data.partiesInterested[partyId]) {
      const update = { ...data.partiesInterested };
      delete update[partyId];
      await oldDocRef.update({ partiesInterested: update });
    }
  }
}

// Delete Issue Register item supporting both new and old layouts
export async function deleteIssueRegisterItem(db, companyID, issueId, origin) {
  // Try new layout first
  try {
    const registerDocRef = db.collection('customers')
      .doc(companyID)
      .collection('product')
      .doc('managementSystemGuide')
      .collection('issueRegister')
      .doc('register');
    const regSnap = await registerDocRef.get();
    if (regSnap.exists && regSnap.data()[issueId]) {
      await registerDocRef.update({ [issueId]: firebaseImport.firestore.FieldValue.delete() });
      return;
    }
  } catch (e) {
    // Continue to old layout
  }
  // Old layout: issuesInternal/issuesExternal in ismsManual
  if (origin && origin.oldDocument && origin.map && origin.issueKey) {
    const oldDocRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);
    const oldDocSnap = await oldDocRef.get();
    if (oldDocSnap.exists) {
      const data = oldDocSnap.data();
      if (data[origin.map] && data[origin.map][origin.issueKey]) {
        const update = { ...data[origin.map] };
        delete update[origin.issueKey];
        await oldDocRef.update({ [origin.map]: update });
      }
    }
  }
}

export function fetchInterestedParties(db, companyID, setInterestedParties) {
  console.log('[/msg/utils/common.ts:fetchInterestedParties]', 'init', companyID);

  const docRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);
  console.log('[/msg/utils/common.ts:fetchInterestedParties]', docRef);

  const unsubscribe = docRef.onSnapshot(async (doc) => {
    if(!doc.exists) {
      console.log('[/msg/utils/common.ts:fetchInterestedParties]', 'No such document found');
      return null;
    }

    const data = doc.data() as ISMSManual;

    const partiesInterested = data.partiesInterested || {};
    setInterestedParties(Object.entries(partiesInterested).map(([id, value]) => ({
      id,
      ...value,
    })));
    console.log('[/msg/utils/common.ts:fetchInterestedParties]', partiesInterested);

  }, (error) => {
    console.error('Error getting document:', error);
  });

  return unsubscribe;
}

interface InterestedParty {
  dateAdded?: firebaseImport.firestore.Timestamp;
  dateUpdated?: firebaseImport.firestore.Timestamp;
  party?: string;
  requirement?: string;
  requirementsAddressedThroughISMS?: string;
  updatedBy?: string;
  frameworks?: string[];
  // Additional properties can be added as needed
}

interface InterestedParties {
  [key: string]: InterestedParty;
}

interface ISMSManual {
  partiesInterested?: InterestedParties;
  [key: string]: any;
  // Additional properties can be added as needed
}
